import React from "react";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import {
  CftBox,
  CftLabel,
  CftFormField,
  CftError,
  CftFormControl,
  CftBoxWrapper,
  CftConditionalRendering,
  CftTextField,
  CftSelectField,
  CftTooltip,
} from "../../../components";

// Energy types Icons
import { ReactComponent as PlowingIcon } from "../../../../assets/icons/cft/orka.svg";
import { ReactComponent as SowingIcon } from "../../../../assets/icons/cft/sianie.svg";
import { ReactComponent as HarvestIcon } from "../../../../assets/icons/cft/zniwa.svg";
import { ReactComponent as DieselIcon } from "../../../../assets/icons/cft/diesel.svg";
import { ReactComponent as GasIcon } from "../../../../assets/icons/cft/benzyna.svg";

import { machineryTypes } from "./fuelAndEnergyFarmlands.consts";

function EnergyConsumptionFields({ id, name }) {
  const { setFieldValue, values } = useFormikContext();
  const {
    t,
    i18n: { language },
  } = useTranslation(["cft"]);

  const machineryType = values[name][id].type;

  return (
    <>
      <CftFormControl>
        <CftLabel
          label={t("cft:fuelAndEnergy.form.1", "Wybierz typ maszyny")}
        />
        <CftFormField name={`${name}[${id}].type`}>
          {({ value, helpers, meta }) => (
            <>
              <CftBoxWrapper>
                <CftBox
                  isActive={value === "plowing"}
                  onClick={() => {
                    helpers.setValue("plowing");
                    setFieldValue(`${name}[${id}].machineryType`, "");
                  }}
                  isInvalid={meta.touched && meta.error}
                  variant="medium"
                >
                  <PlowingIcon className="cft-box__svg-fix" />
                  <p className="cft-box__paragraph">
                    {t("cft:fuelAndEnergy.form.2", "Do roki")}
                  </p>
                </CftBox>
                <CftBox
                  isActive={value === "sowing"}
                  onClick={() => {
                    helpers.setValue("sowing");
                    setFieldValue(`${name}[${id}].machineryType`, "");
                  }}
                  isInvalid={meta.touched && meta.error}
                  variant="medium"
                >
                  <SowingIcon className="cft-box__svg-fix" />
                  <p className="cft-box__paragraph">
                    {t("cft:fuelAndEnergy.form.3", "Do siania")}
                  </p>
                </CftBox>
                <CftBox
                  isActive={value === "harvest"}
                  onClick={() => {
                    helpers.setValue("harvest");
                    setFieldValue(`${name}[${id}].machineryType`, "");
                  }}
                  isInvalid={meta.touched && meta.error}
                  variant="medium"
                >
                  <HarvestIcon className="cft-box__svg-fix" />
                  <p className="cft-box__paragraph">
                    {t("cft:fuelAndEnergy.form.4", "Do żniw")}
                  </p>
                </CftBox>
              </CftBoxWrapper>
              <CftError
                isActive={meta.touched && meta.error}
                message={meta.error}
                textAlign="center"
              />
            </>
          )}
        </CftFormField>
      </CftFormControl>
      <CftConditionalRendering isConditionFulfilled={!!machineryType}>
        <CftFormControl variant="small">
          <CftLabel
            label={t("cft:fuelAndEnergy.form.5", "Wybierz maszynę")}
            name={`${name}[${id}].machineryType`}
          />
          <CftSelectField
            name={`${name}[${id}].machineryType`}
            options={
              machineryTypes[machineryType]
                ? machineryTypes[machineryType].map(option => ({
                    label: option[language],
                    value: option.value,
                  }))
                : []
            }
          />
        </CftFormControl>
      </CftConditionalRendering>
      <CftFormControl>
        <CftLabel
          label={t("cft:fuelAndEnergy.form.6", "Wybierz rodzaj napędu")}
          // eslint-disable-next-line react/no-children-prop
          children={
            <CftTooltip
              content={t(
                "cft:fuelAndEnergy.tooltips.1",
                "Określ rodzaj paliwa dla danego transportu"
              )}
            />
          }
        />
        <CftFormField name={`${name}[${id}].machineryFuel`}>
          {({ value, helpers, meta }) => (
            <>
              <CftBoxWrapper>
                <CftBox
                  isActive={value === "102"}
                  onClick={() => {
                    helpers.setValue("102");
                  }}
                  isInvalid={meta.touched && meta.error}
                >
                  <DieselIcon className="cft-box__svg-fix" />
                  <p className="cft-box__paragraph">
                    {t("cft:fuelAndEnergy.form.7", "Diesel")}
                  </p>
                </CftBox>
                <CftBox
                  isActive={value === "103"}
                  onClick={() => {
                    helpers.setValue("103");
                  }}
                  isInvalid={meta.touched && meta.error}
                >
                  <GasIcon className="cft-box__svg-fix" />
                  <p className="cft-box__paragraph">
                    {t("cft:fuelAndEnergy.form.8", "Benzyna")}
                  </p>
                </CftBox>
              </CftBoxWrapper>
              <CftError
                isActive={meta.touched && meta.error}
                message={meta.error}
                textAlign="center"
              />
            </>
          )}
        </CftFormField>
      </CftFormControl>
      <CftFormControl variant="small">
        <CftLabel label={t("cft:fuelAndEnergy.form.9", "Zużycie")} />
        <CftTextField
          name={`${name}[${id}].liters`}
          adornment={t("cft:fuelAndEnergy.form.0", "litrów")}
        />
      </CftFormControl>
      <CftFormControl variant="small">
        <CftLabel
          label={t("cft:fuelAndEnergy.form.11", "Wpisz liczbę operacji")}
          // eslint-disable-next-line react/no-children-prop
          children={
            <CftTooltip
              content={t(
                "cft:fuelAndEnergy:tooltips.2",
                "Wskaż całkowitą liczbę operacji wykonaną na tej uprawie"
              )}
            />
          }
        />
        <CftTextField name={`${name}[${id}].operations`} />
      </CftFormControl>
    </>
  );
}

export default EnergyConsumptionFields;
